import { UploadOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Switch,
  Upload,
} from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  createProduct,
  getAllRewards,
  getProduct,
  getProductsCurrencies,
  updateProduct,
  uploadProductImage,
} from "../actions/acc_actions";
import ContentLayout from "../components/layout/ContentLayout";
import { generateRandomString } from "../utils/functions";

export default function ShopForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [imageData, setImageData] = useState({ imgUrl: null, imgId: null });
  const [isUnlimited, setIsUnLimited] = useState(false);
  const businessUrn = useSelector((state) => state.account.businessUrn);
  const businessSlug = useSelector((state) => state.account.businessSlug);

  const { data: rewardsData } = useQuery({
    queryKey: ["rewards", businessUrn],
    queryFn: () => getAllRewards(businessUrn),
    enabled: !!businessUrn,
  });

  const { data: productData } = useQuery({
    queryKey: ["product", [id]],
    queryFn: () => getProduct(id),
    enabled: !!id,
  });

  const { data: productsCurrencies } = useQuery({
    queryKey: ["productsCurrencies"],
    queryFn: () => getProductsCurrencies(),
    enabled: !!businessUrn,
  });

  useEffect(() => {
    if (productData) {
      const product = productData.body;
      form.setFieldsValue({
        name_en: product.name.en,
        description_en: product.description.en,
        stock: product.is_unlimited ? 0 : product.stock,
        is_unlimited: product.is_unlimited,
        is_active: product.is_active,
        price: product.price,
        currency_code: product.currency_code,
        reward_type_slug: product.reward_type_slug,
        voucher_quantity: product.voucher_quantity,
      });
      setImageData({
        imgUrl: product.image?.path || null,
        imgId: product.image?.id || null,
      });
    }
  }, [productData, form]);

  const onFinish = (values) => {
    const formData = {
      ...values,
      imageId: imageData.imgId,
      name_ar: values.name_en,
      description_ar: values.description_en,
      name_en: values.name_en,
      description_en: values.description_en,
      voucher_quantity: values.voucher_quantity,
      is_unlimited: values.is_unlimited || false,
      stock: values.is_unlimited ? 0 : values.stock,
    };

    if (id) {
      updateProduct(id, formData)
        .then(() => {
          navigate("/shop");
          message.success(t("product_updated_success"));
        })
        .catch(() => message.error(t("something_went_wrong")));
    } else {
      createProduct({
        ...formData,
        sku: generateRandomString(),
        business_slug: businessSlug,
        voucher_scheme_slug: businessSlug + "-vouchers",
        client_slug: "magic-stamp",
      })
        .then(() => {
          navigate("/shop");
          message.success(t("product_created_success"));
        })
        .catch((err) => {
          const apiErrors = err?.response?.data?.error?.errors;

          if (Array.isArray(apiErrors)) {
            apiErrors.forEach((e) => message.error(e.msg));
          } else {
            message.error(t("something_went_wrong"));
          }
        });
    }
  };

  const onFinishFailed = () => {
    message.error(t("fill_required_fields"));
  };

  const handleUpload = ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("model", "PRODUCT");

    uploadProductImage(formData)
      .then((res) => {
        const uploadedImageUrl = res?.data?.body.url;
        const uploadedImageId = res?.data?.body.id;
        onSuccess("ok");
        setImageData({ imgUrl: uploadedImageUrl, imgId: uploadedImageId });
        message.success(t("upload") + " successful!");
      })
      .catch((error) => {
        onError(error);
        message.error(t("only_images_allowed"));
      });
  };

  const selectedCurrency = Form.useWatch("currency_code", form);

  return (
    <ContentLayout
      header={
        <>
          <h1>
            {t("add_product")} <span>{id ? t("update_product") : ""}</span>
          </h1>
          <p className="content_layout-subtitle">{t("add_product_subtitle")}</p>
        </>
      }
    >
      <Form
        form={form}
        className="shop-form"
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        {/* Rewards & Vouchers */}
        <div className="shop-form-section">
          <h5 className="active">{t("rewards_vouchers")}</h5>
          <div className="rewards-inputs">
            <Form.Item
              name="reward_type_slug"
              label={t("reward_type")}
              rules={[
                { required: true, message: t("please_select_reward_type") },
              ]}
            >
              <Select
                placeholder={t("reward_type")}
                options={rewardsData?.body?.map((item) => ({
                  label: item.name,
                  value: item.slug,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="voucher_quantity"
              label={t("quantity")}
              rules={[
                { required: true, message: t("please_enter_description") },
              ]}
            >
              <InputNumber min={1} />
            </Form.Item>
          </div>
        </div>

        {/* Basic Info */}
        <div className="shop-form-section">
          <h5 className="active">{t("basic_info")}</h5>
          <div className="rewards-inputs">
            <Form.Item
              label={t("product_image")}
              rules={[{ required: true, message: t("please_upload_image") }]}
            >
              <Upload
                customRequest={handleUpload}
                listType="picture"
                maxCount={1}
                beforeUpload={(file) => {
                  const isImage = file.type.startsWith("image/");
                  if (!isImage) message.error(t("only_images_allowed"));
                  return isImage;
                }}
              >
                <Button icon={<UploadOutlined />}>{t("upload")}</Button>
              </Upload>
              {imageData.imgUrl && (
                <img
                  src={imageData.imgUrl}
                  alt="Uploaded"
                  style={{ marginTop: 10, maxWidth: "100px" }}
                />
              )}
            </Form.Item>{" "}
            <Form.Item
              name="name_en"
              label={t("name")}
              rules={[{ required: true, message: t("please_enter_name") }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="description_en"
              label={t("description")}
              rules={[
                { required: true, message: t("please_enter_description") },
              ]}
            >
              <Input.TextArea rows={4} />
            </Form.Item>
          </div>
        </div>

        {/* Stock & Pricing */}
        <div className="shop-form-section">
          <h5 className="active">{t("stock_pricing")}</h5>
          <div className="rewards-inputs">
            <div className="form-row">
              <Form.Item
                name="stock"
                label={t("stock_quantity")}
                rules={[
                  { required: !isUnlimited, message: t("please_enter_stock") },
                ]}
              >
                <InputNumber disabled={isUnlimited} min={0} />
              </Form.Item>
              <div style={{ display: "flex", gap: 10 }}>
                <Form.Item
                  name="price"
                  label={t("price")}
                  rules={[{ required: true, message: t("please_enter_price") }]}
                  style={{ flex: 2 }}
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    addonAfter={selectedCurrency || "USD"}
                  />
                </Form.Item>

                <Form.Item
                  name="currency_code"
                  label={t("currency")}
                  rules={[
                    { required: true, message: t("please_select_currency") },
                  ]}
                  style={{ flex: 1 }}
                >
                  <Select
                    placeholder={t("select_currency")}
                    options={(productsCurrencies || []).map((c) => ({
                      label: c,
                      value: c,
                    }))}
                  />
                </Form.Item>
              </div>
              <Form.Item
                name="is_unlimited"
                label={t("unlimited_stock")}
                valuePropName="checked"
              >
                <Switch onChange={(checked) => setIsUnLimited(checked)} />
              </Form.Item>
              <Form.Item
                name="is_active"
                label={t("active")}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="shop-form-btn">
          <Button
            style={{
              width: "100%",
              padding: "2.4rem 0",
              fontSize: "1.8rem",
              fontWeight: "700",
            }}
            htmlType="submit"
            type="primary"
          >
            {id ? t("update_product") : t("add_product_btn")}
          </Button>
        </div>
      </Form>
    </ContentLayout>
  );
}
